/*
 * Lazy hydration for upsell / free-gift popup galleries.
 *
 * Why this exists
 * ---------------
 * Every upsell and free-gift popup on the PDP used to carry its own inline
 * script that ran at page load and did this:
 *
 *   fetch('/products/<handle>?section_id=fa-main-product')
 *
 * That is the ENTIRE main product section -- gallery, buy box, everything --
 * for a product nobody has clicked on yet. On the NordBench Pro PDP that meant
 * seven of these requests firing during load, and each response was appended
 * into the DOM as-is. Measured cost of that on the live page:
 *
 *   - three extra fa-main-product sections in the DOM (~480 nodes) before any
 *     popup was opened
 *   - nine <link rel="preload" as="image"> tags injected at runtime, each
 *     fetching a hero image at high priority in direct competition with the
 *     real LCP image
 *   - eager, fetchpriority="high" images for products the visitor cannot see
 *   - seven section renders on Shopify's side, per page view
 *
 * What it does instead
 * --------------------
 * Nothing at load. When a drawer opens -- either because its trigger was
 * clicked or because the theme fired dialog:after-show -- the gallery inside it
 * is fetched once, cleaned, and appended. Subsequent opens are free.
 *
 * "Cleaned" means: any <link rel="preload"> is dropped and any
 * fetchpriority="high" is removed from the fetched fragment. By the time it is
 * inserted the drawer is visible, so the images load on their own; they just
 * no longer outrank anything else on the page.
 *
 * Contract with the Liquid
 * ------------------------
 * snippets/custom-popup-content.liquid renders the target as
 *
 *   <div class="custom-popup__product-gallery" data-product-handle="..."></div>
 *
 * and this file is loaded once from sections/fa-main-product.liquid. The five
 * snippets that used to carry the inline fetch have had it removed.
 *
 * Preserved from the old scripts: the whole thing is skipped when the page is
 * being viewed on the myshopify.com domain, which is how the old code avoided
 * running inside theme previews.
 */
(function () {
  if (window.__nsPopupGalleryLazy) return;
  window.__nsPopupGalleryLazy = true;

  if (window.location.hostname.includes('myshopify')) return;

  var SELECTOR = '.custom-popup__product-gallery[data-product-handle]';

  function hydrate(target) {
    if (!target) return;

    // Any truthy state means this open has already been handled -- loading,
    // ready, or failed. Back off. 'error' is cleared on close (see below), so
    // a LATER open still retries; what must never happen is a second fetch
    // inside the SAME open.
    if (target.dataset.state) return;

    target.dataset.state = 'loading';

    var handle = target.dataset.productHandle;

    fetch('/products/' + encodeURIComponent(handle) + '?section_id=fa-main-product')
      .then(function (res) {
        if (!res.ok) throw new Error('popup gallery fetch failed: ' + res.status + ' for ' + handle);
        return res.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var section = doc.querySelector('[id^="shopify-section-fa-main-product"]');
        if (!section) throw new Error('popup gallery: no fa-main-product section in response for ' + handle);

        // Never let a popup's imagery outrank the page it sits on.
        section.querySelectorAll('link[rel="preload"]').forEach(function (l) { l.remove(); });
        section.querySelectorAll('[fetchpriority="high"]').forEach(function (el) { el.removeAttribute('fetchpriority'); });

        target.appendChild(section);
        target.dataset.state = 'ready';
      })
      .catch(function (err) {
        // Mark the failure rather than clearing the flag outright.
        //
        // Both the click listener and the dialog:after-show listener call this
        // for the same open. Deleting the flag here meant that when a fetch
        // failed fast -- which it does under Shopify's rate limiting, the very
        // situation where extra requests hurt most -- the second listener saw
        // no flag and fired a duplicate fetch. Two requests per open, exactly
        // when the server is already refusing them.
        //
        // Leaving it set is what makes the sibling listener back off. Retry is
        // handled on close instead, by the dialog:after-hide listener below --
        // an earlier version tried to express "retryable" by exempting 'error'
        // from the guard, which reopened the duplicate-fetch hole it was
        // written to close.
        target.dataset.state = 'error';
        console.error(err);
      });
  }

  function hydrateDrawer(drawer) {
    if (!drawer) return;
    hydrate(drawer.querySelector(SELECTOR));
  }

  // Early path: the click that opens the drawer. Starts the fetch before the
  // open transition finishes.
  document.addEventListener('click', function (event) {
    var trigger = event.target.closest('[aria-controls]');
    if (!trigger) return;
    var drawer = document.getElementById(trigger.getAttribute('aria-controls'));
    if (drawer && drawer.tagName === 'X-DRAWER') hydrateDrawer(drawer);
  }, { passive: true });

  // Fallback: any drawer opened by other means. The theme's x-drawer
  // dispatches this with bubbles: true.
  document.addEventListener('dialog:after-show', function (event) {
    if (event.target && event.target.tagName === 'X-DRAWER') hydrateDrawer(event.target);
  });

  // Arm the retry. A gallery that failed stays flagged for the whole of the
  // open it failed in, so the sibling listener cannot fire a second request at
  // a server that is already refusing them. Once the drawer closes, clear the
  // failure so the next open gets a fresh attempt. 'ready' and 'loading' are
  // deliberately left alone: one is a cached success, the other a fetch still
  // in flight. x-drawer dispatches this with bubbles: true.
  document.addEventListener('dialog:after-hide', function (event) {
    if (!event.target || event.target.tagName !== 'X-DRAWER') return;
    var target = event.target.querySelector(SELECTOR);
    if (target && target.dataset.state === 'error') delete target.dataset.state;
  });
})();
